import { observable, action, runInAction } from "mobx";
import moment from "moment";
import http from "../../util/http";
import slack from "../../util/slack";
import _ from "lodash";

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

class ChaseListStore {
  @observable chaseList = [];
  @observable phones = [];
  @observable defaultPhone = "";
  @observable phoneToAdd = "";
  @observable feedbackMsg = "";
  @observable textbox = "";
  @observable chaseObj = {};
  @observable isDialogOpen = false;
  @observable totalChase = 0;
  @observable selectAllProp = false;
  @observable isBulkOpen = false;
  @observable addPhoneOpen = false;
  @observable bulkTextbox = "";
  @observable timeInterval = 12;
  @observable totalBulkMessages = 0;
  @observable totalBulkMessagesSent = 0;
  @observable currentSendingAgent = "";
  @observable bulkInProgress = false;

  @action
  setTimeInterval(time) {
    this.timeInterval = time;
  }

  @action
  toggleAddNumber() {
    this.addPhoneOpen = !this.addPhoneOpen;
  }

  @action
  setBulkMessage(msg) {
    this.bulkTextbox = msg;
  }

  @action
  async getPhones(IdAgent) {
    try {
      const phones = await http.sendHelperCommand(
        "ChaseListGetPhonesHelper.aspx",
        {
          IdAgent,
        }
      );

      runInAction(() => {
        const phoneObj = _.find(phones, { isDefault: "True" });
        this.defaultPhone = _.get(phoneObj, "phone", "");
        this.phones = phones;
        if (_.isEmpty(phones)) {
          this.addPhoneOpen = true;
        }
      });
    } catch (err) {
      console.log(err);
    }
  }

  @action
  toggleBulkDialog() {
    this.isBulkOpen = !this.isBulkOpen;
    this.bulkInProgress = false;
    if (this.isBulkOpen) {
      const selectedAgents = _.filter(
        this.chaseList,
        (item) => item.Phone !== "" && item.selected !== false
      );
      this.totalBulkMessages = _.size(selectedAgents);
    } else {
      this.totalBulkMessages = 0;
      this.currentSendingAgent = "";
      this.bulkTextbox = "";
      this.totalBulkMessagesSent = 0;
    }
  }

  @action
  toggleDialog(chaseObj) {
    this.isDialogOpen = !this.isDialogOpen;
    if (chaseObj) {
      this.chaseObj = chaseObj;
      this.getPhones(chaseObj.IdAgent);
    }

    if (!this.isDialogOpen) {
      this.textbox = "";
      this.feedbackMsg = "";
    }
  }

  @action
  toggleCheckbox(index) {
    this.chaseList[index].selected = !this.chaseList[index].selected;
  }

  @action
  selectAll() {
    this.selectAllProp = !this.selectAllProp;
    this.chaseList = _.map(this.chaseList, (chase) => {
      chase.selected = this.selectAllProp;
      return chase;
    });
  }

  @action
  async getChaseList(IdAgent) {
    try {
      const chaseList = await http.sendHelperCommand("ChaseListHelper.aspx", {
        IdAgent,
      });

      runInAction(() => {
        this.chaseList = _.map(_.uniqBy(chaseList, "IdAgent"), (item) => {
          return {
            ...item,
            bulkSMS: item.BulkSMS === "True" ? true : false,
            due: _.toNumber(item.CurrentBalance.replace(/\D/g, "")),
            selected: false,
          };
        });

        this.totalChase = _.sum(
          _.map(chaseList, (item) =>
            _.toNumber(item.CurrentBalance.replace(/\D/g, ""))
          )
        );
      });
    } catch (err) {
      runInAction(() => {
        this.chaseList = [];
      });
    }
  }

  @action
  async addPhoneNumber(chaseObject) {
    try {
      const phone = this.phoneToAdd;
      const cleanPhone = ("" + phone).replace(/\D/g, "").substring(0, 11);
      if (_.size(cleanPhone) === 11) {
        const newNumber = await http.sendHelperCommand(
          "ChaseListAddNumberHelper.aspx",
          {
            IdAgent: chaseObject.IdAgent,
            phone: cleanPhone.substr(1),
            name: `${chaseObject.Agent}/${moment().format()}`,
            areaCode: 1,
          }
        );
        if (newNumber === 1) {
          this.getPhones(chaseObject.IdAgent);
          runInAction(() => {
            this.phoneToAdd = "";
          });
        }
      }
    } catch (err) {
      console.log(err);
    }
  }

  @action
  setDefaultPhone(phone, fromSelect) {
    if (fromSelect) {
      this.defaultPhone = phone;
    } else {
      this.phoneToAdd = phone;
    }
  }

  @action
  setMessage(message) {
    this.textbox = message;
  }

  @action
  async sendSMS() {
    const chaseToSend = {
      player: this.chaseObj.Agent,
      recipient: ("" + this.defaultPhone).replace(/\D/g, "").substring(0, 11),
      body: this.textbox,
    };

    try {
      const smsData = await http.sendHelperCommand(
        "ChaseSendSMSHelper.aspx",
        chaseToSend
      );
      runInAction(() => {
        this.feedbackMsg = _.isObject(smsData)
          ? "sms successfully sent to the recipient!"
          : "something went wrong, sms was not sent";
      });

      if (!_.isObject(smsData)) {
        await slack.notify(
          "ups an error happen with text request in chaselist page",
          chaseToSend,
          smsData
        );
      }
    } catch (err) {
      runInAction(() => {
        this.feedbackMsg = "something went wrong, sms was not sent";
      });
      await slack.notify(
        "ups an error happen with text request in chaselist page",
        chaseToSend,
        { error: `${err.name}-${err.message}` }
      );
    }
  }

  @action
  setFeedback(msg) {
    this.feedbackMsg = msg;
  }

  @action
  async resetBulkChecks(IdAgent) {
    try {
      await http.sendHelperCommand("ChaseListResetChecksHelper.aspx");
      await this.getChaseList(IdAgent);
    } catch (error) {
      console.log(error);
    }
  }

  @action
  async sendSMSBulk() {
    try {
      this.bulkInProgress = true;
      const selectedAgents = _.map(
        _.filter(
          this.chaseList,
          (item) => item.Phone !== "" && item.selected !== false
        ),
        (item) => {
          item.bulkSMS = false;
          return item;
        }
      );

      if (!_.isEmpty(selectedAgents)) {
        await http.sendHelperCommand("ChaseListResetChecksHelper.aspx");

        for (let chase of selectedAgents) {
          const chaseIndex = _.indexOf(this.chaseList, chase);

          if (chaseIndex !== -1) {
            this.currentSendingAgent = chase.Agent;
            const chaseToSend = {
              player: chase.Agent,
              recipient: `1${chase.Phone}`,
              body: this.bulkTextbox,
              idAgent: chase.IdAgent,
            };

            const smsData = await http.sendHelperCommand(
              "ChaseSendSMSHelper.aspx",
              chaseToSend
            );
            if (!_.isObject(smsData)) {
              await slack.notify(
                "ups an error happen with text request in chaselist page",
                chaseToSend,
                smsData
              );
            }

            this.chaseList[chaseIndex].bulkSMS = true;
            this.totalBulkMessagesSent = _.size(
              _.filter(this.chaseList, { bulkSMS: true })
            );

            await slack.notify(
              "Bulk message succesfully sent.",
              smsData,
              this.totalBulkMessagesSent
            );
            await sleep(15000);
          }
        }
      } else {
        this.bulkInProgress = false;
      }
    } catch (err) {
      this.bulkInProgress = false;
      await slack.notify(
        "ups an error happen with text request in chaselist page",
        {},
        { error: `${err.name}-${err.message}` }
      );
    }
  }
}

export default new ChaseListStore();
